export const THEMES = ['dark', 'light'];
export const PALETTES = ['standard', 'okabe-ito'];
export const isPaletteName = (v) => typeof v === 'string' && PALETTES.includes(v);
/** The empty board under the tokens, per theme. */
export const BOARD_BG = { dark: '#1c1917', light: '#f5f5f4' };
/** The shell behind the dock, per theme. */
export const SHELL_BG = { dark: '#0c0a09', light: '#fafaf9' };
/** The site's named themes (client/src/config/theme.js: dark, light, high-contrast, colorblind): only light is light. */
export const tableTheme = (siteTheme) => (siteTheme === 'light' ? 'light' : 'dark');
/** The person's palette: their own choice, or, never chosen, the safe one when the site is set to its colour-blind theme. */
export const paletteFor = (chosen, siteTheme) => chosen ?? (siteTheme === 'colorblind' ? 'okabe-ito' : 'standard');
/** The markers' colours (issue 855) as the standard palette draws them. */
export const MARKER_HEX = {
    red: '#dc2626', blue: '#2563eb', green: '#16a34a', yellow: '#facc15', purple: '#9333ea', orange: '#ea580c',
};
/** Okabe & Ito's colour-blind-safe set (2008) for the markers, one hue each. */
const SAFE_MARKERS = {
    red: '#d55e00', blue: '#0072b2', green: '#009e73', yellow: '#f0e442', purple: '#cc79a7', orange: '#e69f00',
};
/**
 * The twelve identity colours (`PALETTE`) in the safe palette: Okabe–Ito's seven hues, then Paul Tol's for the five
 * that set has no room for — told apart under the common colour-vision deficiencies.
 */
const SAFE_IDENTITY = {
    '#c0392b': '#d55e00', '#e67e22': '#e69f00', '#f1c40f': '#f0e442', '#27ae60': '#009e73', '#16a085': '#44aa99', '#2980b9': '#56b4e9',
    '#5b4fcf': '#0072b2', '#8e44ad': '#aa3377', '#e84393': '#cc79a7', '#ff7f50': '#ee6677', '#8bc34a': '#999933', '#7f8c8d': '#bbbbbb',
};
const channels = (hex) => [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16));
const toHex = (rgb) => `#${rgb.map((c) => Math.round(Math.max(0, Math.min(255, c))).toString(16).padStart(2, '0')).join('')}`;
/** WCAG 2.x relative luminance of a `#rrggbb` colour. */
export function relativeLuminance(hex) {
    const [r, g, b] = channels(hex).map((c) => { const s = c / 255; return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4; });
    return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}
/** WCAG 2.x contrast ratio between two `#rrggbb` colours, 1 to 21. */
export function contrastRatio(a, b) {
    const [hi, lo] = [relativeLuminance(a), relativeLuminance(b)].sort((x, y) => y - x);
    return (hi + 0.05) / (lo + 0.05);
}
/** A colour held to `min`:1 against `bg`: as it is when it passes, else mixed towards white on a dark board or black on a light one. */
export function withContrast(hex, bg, min = 3) {
    if (contrastRatio(hex, bg) >= min)
        return hex.toLowerCase();
    const towards = relativeLuminance(bg) < 0.5 ? 255 : 0;
    const rgb = channels(hex);
    for (let k = 1; k <= 20; k += 1) {
        const mixed = toHex(rgb.map((c) => c + (towards - c) * (k / 20)));
        if (contrastRatio(mixed, bg) >= min)
            return mixed;
    }
    return towards ? '#ffffff' : '#000000';
}
/** An identity colour as this person's board draws it: their palette, then held to 3:1 on the board in their theme. */
export const displayColour = (hex, palette, theme) => withContrast(palette === 'okabe-ito' ? SAFE_IDENTITY[hex.toLowerCase()] ?? hex : hex, BOARD_BG[theme]);
/** A marker's colour as this person's board draws it. */
export const markerHex = (m, palette, theme) => withContrast(palette === 'okabe-ito' ? SAFE_MARKERS[m] : MARKER_HEX[m], BOARD_BG[theme]);
/**
 * The side outlines' colours (qa-runs#1157): green for a Friend, blue for Neutral, red for a Foe — the markers' own
 * hues, so the colour-blind-safe palette draws them in its green, blue and red.
 */
export const SIDE_MARKER = { friend: 'green', neutral: 'blue', foe: 'red' };
/** A side's outline colour as this person's board draws it. */
export const sideHex = (d, palette, theme) => markerHex(SIDE_MARKER[d], palette, theme);
//# sourceMappingURL=palette.js.map